/**
 * SystemicPathwaysCard — Phase C Section 7.
 *
 * Educational pathway maps for Diabetes ↔ Oral, Obesity ↔ Oral, CVD ↔ Oral.
 * Each pathway shows a 3-node chain and the personalised "strength" indicator
 * derived from the user's current metabolic state.
 */
import React from "react";
import { Network, ArrowDown } from "lucide-react";

export default function SystemicPathwaysCard({ data, isDark, lang = "bg", cardCls }) {
  const t = {
    bg: {
      title: "Системно-орални пътища",
      desc:  "Образователни асоциации (не причинност). Стрелките показват колко силно е активиран пътят при вас.",
      strength: "Активност",
    },
    en: {
      title: "Systemic-oral pathways",
      desc:  "Educational associations (not causation). Bars indicate how strongly the pathway is active for you.",
      strength: "Activity",
    },
    es: {
      title: "Vías sistémico-orales",
      desc:  "Asociaciones educativas (no causalidad). Las barras indican con qué intensidad la vía está activa para usted.",
      strength: "Actividad",
    },
  }[lang] || {};

  if (!data) return null;

  const pathways = [
    { key: "diabetes_oral", color: "from-amber-500 to-rose-500" },
    { key: "obesity_oral",  color: "from-orange-500 to-pink-500" },
    { key: "cvd_oral",      color: "from-red-500 to-purple-500" },
  ];

  return (
    <div className={`${cardCls} p-5`} data-testid="dhi-systemic-pathways-card">
      <div className="flex items-center gap-2 mb-1">
        <Network className="w-5 h-5 text-indigo-500" />
        <h3 className="text-lg font-bold">{t.title}</h3>
      </div>
      <p className="text-xs opacity-60 mb-4">{t.desc}</p>

      <div className="grid md:grid-cols-3 gap-3">
        {pathways.map(({ key, color }) => {
          const p = data[key];
          if (!p) return null;
          const chain = lang === "en" ? p.chain_en : p.chain_bg;
          return (
            <div
              key={key}
              className={`relative overflow-hidden rounded-xl p-3 border ${
                isDark ? "border-white/10 bg-white/[0.02]" : "border-gray-200 bg-gray-50"
              }`}
              data-testid={`dhi-pathway-${key}`}
            >
              {/* Activity bar */}
              <div className="absolute inset-x-0 top-0 h-1 bg-black/10">
                <div
                  className={`h-full bg-gradient-to-r ${color}`}
                  style={{ width: `${p.strength}%` }}
                  data-testid={`dhi-pathway-strength-${key}`}
                />
              </div>
              <div className="text-[11px] font-bold mb-2 pt-1">
                {lang === "en" ? p.label_en : p.label_bg}
              </div>
              <div className="space-y-1">
                {chain.map((step, idx) => (
                  <React.Fragment key={idx}>
                    <div className={`text-xs px-2 py-1.5 rounded text-center font-medium ${
                      isDark ? "bg-white/5" : "bg-white"
                    }`}>
                      {step}
                    </div>
                    {idx < chain.length - 1 && (
                      <ArrowDown className="w-3 h-3 mx-auto opacity-40" />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <div className="mt-2 flex items-center justify-between text-[10px] opacity-60">
                <span>{t.strength}: {p.strength}%</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
